import React, { useCallback, useEffect, useState } from 'react';
import { Users, Globe2, RefreshCw, AlertCircle, Fingerprint, Unlink } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
  Badge, Button, Alert, AlertTitle, AlertDescription
} from '../ui';
import {
  getPlayersInMultipleClubs,
  getGlobalIdentitiesMulti,
  unlinkFichaGlobalPlayer,
  reconcileAllGlobalPlayers,
} from '../../lib/supabase';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { fixMojibake, cn } from '../../lib/utils';

const PlatformCrossOrg = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data, error } = await getPlayersInMultipleClubs();
      if (error) throw error;
      setData(data || []);
    } catch (e) {
      console.error(e);
      setErr('No pudimos cargar los datos cross-org.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [identidades, setIdentidades] = useState([]);
  const [loadingId, setLoadingId] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const loadIdentidades = useCallback(async () => {
    setLoadingId(true);
    try {
      const { data, error } = await getGlobalIdentitiesMulti();
      if (error) throw error;
      setIdentidades(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingId(false);
    }
  }, []);

  useEffect(() => { loadIdentidades(); }, [loadIdentidades]);

  const handleSeparar = async (ficha) => {
    const ok = await confirm(
      'Separar ficha',
      `¿Separar la ficha "${fixMojibake(ficha.nombre_completo || '')}" de esta identidad? Quedará como persona aparte y no se volverá a agrupar automáticamente.`,
      { confirmText: 'Separar' }
    );
    if (!ok) return;
    const { error } = await unlinkFichaGlobalPlayer(ficha.jugador_id);
    if (!error) loadIdentidades();
  };

  const handleReconciliar = async () => {
    setReconciling(true);
    try {
      await reconcileAllGlobalPlayers();
      await loadIdentidades();
    } finally {
      setReconciling(false);
    }
  };

  return (
    <FeaturedLayout title="Cross-Org · Platform Admin">
      <div className="space-y-4">
        <Card className="border shadow-sm">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <Globe2 className="h-5 w-5 text-blue-600" />
                Vista cross-org
              </CardTitle>
              <CardDescription>
                Jugadores con membresías activas en más de un club. Útil para detectar
                duplicados, transferencias y conflictos de elegibilidad.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>
          </CardHeader>
        </Card>

        {err && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Jugadores en múltiples clubes
              <Badge variant="secondary">{data.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
            ) : data.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Por ahora ningún jugador pertenece a más de un club.
              </p>
            ) : (
              data.map((row) => (
                <div key={row.jugadorId} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center gap-3">
                    {row.jugador?.imagen_url ? (
                      <img
                        src={row.jugador.imagen_url}
                        alt={row.jugador.nombre_completo || ''}
                        className="h-10 w-10 rounded-full object-cover border"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-muted border flex items-center justify-center text-xs font-semibold">
                        {(row.jugador?.nombre_completo || '?').slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {fixMojibake(row.jugador?.nombre_completo || 'Sin nombre')}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {row.memberships.length} clubes activos
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-12">
                    {row.memberships.map((m) => (
                      <Badge
                        key={`${m.jugador_id}-${m.org_id}`}
                        variant={m.es_principal ? 'default' : 'outline'}
                        className="text-[11px]"
                      >
                        {fixMojibake(m.organizations?.name || 'Org')}
                        {m.es_principal && ' · principal'}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border shadow-sm">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Fingerprint className="h-4 w-4 text-violet-600" />
                Identidades globales
                <Badge variant="secondary">{identidades.length}</Badge>
              </CardTitle>
              <CardDescription>
                Personas detectadas en más de una ficha. El agrupado es automático;
                aquí solo puedes separar una ficha mal agrupada.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleReconciliar} disabled={reconciling}>
              <RefreshCw className={cn('h-4 w-4 mr-1', reconciling && 'animate-spin')} />
              Re-ejecutar
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingId ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
            ) : identidades.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Ninguna persona aparece en más de una ficha por ahora.
              </p>
            ) : (
              identidades.map((ident) => (
                <div key={ident.global_player_id} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">
                      {fixMojibake(ident.display_nombre || 'Sin nombre')}
                    </p>
                    <Badge variant="outline" className="text-[11px]">
                      {ident.ficha_count} fichas
                    </Badge>
                    {ident.curp_norm && (
                      <span className="text-[11px] text-muted-foreground">CURP: {ident.curp_norm}</span>
                    )}
                  </div>
                  <div className="space-y-1 pl-2">
                    {(ident.fichas || []).map((f) => (
                      <div key={f.jugador_id} className="flex items-center justify-between gap-2">
                        <span className="text-[12px] truncate">
                          {fixMojibake(f.org_name || 'Org')} · {fixMojibake(f.nombre_completo || '')}
                          {f.identity_locked && (
                            <Badge variant="secondary" className="ml-2 text-[10px]">bloqueada</Badge>
                          )}
                        </span>
                        <Button
                          variant="ghost" size="sm"
                          className="h-7 text-[11px] text-destructive"
                          onClick={() => handleSeparar(f)}
                        >
                          <Unlink className="h-3.5 w-3.5 mr-1" />
                          Separar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <ConfirmDialog />
      </div>
    </FeaturedLayout>
  );
};

export default PlatformCrossOrg;
